// Admin Console tabs: Users/CRM, Submissions queue, Promo codes.
import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Check, X, ExternalLink, Mail, Share2 } from "lucide-react";

const inr = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;
const border = { borderColor: "var(--wofy-border)" };

export function UsersTab() {
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [nearLimit, setNearLimit] = useState(false);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [blastOpen, setBlastOpen] = useState(false);
  const load = useCallback((query, planF = plan, nl = nearLimit) => {
    const params = new URLSearchParams({ q: query || "" });
    if (planF) params.set("plan", planF);
    if (nl) params.set("near_limit", "true");
    http.get(`/admin/console/users?${params}`).then((r) => setData(r.data)).catch(() => {});
  }, [plan, nearLimit]);
  useEffect(() => { load(q); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (uid) => {
    const { data: d } = await http.get(`/admin/console/users/${uid}`);
    setDetail(d);
  };
  const grant = async (uid, plan_key) => {
    try {
      await http.post(`/admin/console/users/${uid}/grant-plan`, { plan_key });
      toast.success(`Plan set to ${plan_key}`);
      openDetail(uid); load(q);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 flex-wrap items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="wofy-input flex-1 min-w-[180px]" data-testid="admin-users-search" />
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className="wofy-input w-auto" data-testid="admin-users-plan-filter">
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="advance">Advance</option>
          <option value="pro">Pro</option>
          <option value="unlimited">Unlimited</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none" data-testid="admin-users-nearlimit-label">
          <input type="checkbox" checked={nearLimit} onChange={(e) => setNearLimit(e.target.checked)} data-testid="admin-users-nearlimit" />
          Near limit (≥80%)
        </label>
        <button className="wofy-btn-primary" data-testid="admin-users-search-btn"><Search className="w-4 h-4" /> Filter</button>
        <button type="button" onClick={() => setBlastOpen(true)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border" style={{ borderColor: "#16A34A", color: "#166534" }} data-testid="admin-users-email-segment-btn">
          <Mail className="w-4 h-4" /> Email this segment
        </button>
      </form>
      {!data ? <div className="wofy-card text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <div className="wofy-card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] font-mono uppercase opacity-60 border-b" style={border}>
              <th className="px-3 py-2.5">Name</th><th className="px-3 py-2.5">Email</th><th className="px-3 py-2.5">Plan</th>
              <th className="px-3 py-2.5">Trees</th><th className="px-3 py-2.5">Last activity</th></tr></thead>
            <tbody>
              {(data.items || []).map((u) => (
                <tr key={u.id} onClick={() => openDetail(u.id)} className="border-b last:border-0 cursor-pointer hover:bg-amber-50/50" style={border} data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-3 py-2.5">{u.name || "—"}{u.is_admin ? <span className="ml-1 text-[9px] font-mono uppercase text-red-600">admin</span> : null}</td>
                  <td className="px-3 py-2.5 text-xs">{u.email}</td>
                  <td className="px-3 py-2.5"><span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={u.plan_key === "free" ? { background: "#F1F5F9" } : { background: "#DCFCE7", color: "#166534" }}>{u.plan_name}</span></td>
                  <td className="px-3 py-2.5">{u.trees_owned}</td>
                  <td className="px-3 py-2.5 text-xs opacity-70">{(u.last_activity || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-xs opacity-60">{data.total} user(s)</div>
        </div>
      )}

      {blastOpen && (
        <EmailBlastModal
          segment={{ plan_key: plan || null, near_limit: nearLimit, q }}
          onClose={() => setBlastOpen(false)}
        />
      )}

      {detail && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="max-w-lg w-full rounded-2xl bg-white p-6 shadow-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="admin-user-detail">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-display text-2xl">{detail.user.name || detail.user.email}</h3>
                <div className="text-xs opacity-60">{detail.user.email} · joined {(detail.user.created_at || "").slice(0, 10)}</div>
              </div>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase opacity-60 mb-1">Plan (click to grant)</div>
              <div className="flex gap-1.5 flex-wrap">
                {["free", "advance", "pro", "unlimited"].map((k) => {
                  const active = (detail.subscription?.plan_key || "free") === k;
                  return (
                    <button key={k} onClick={() => grant(detail.user.id, k)}
                      className="rounded-full px-3 py-1 text-xs border font-semibold"
                      style={active ? { background: "var(--wofy-primary)", color: "#fff", borderColor: "var(--wofy-primary)" } : border}
                      data-testid={`admin-grant-${k}`}>
                      {k}{active ? " ✓" : ""}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase opacity-60 mb-1">Trees</div>
              {(detail.trees || []).map((t) => (
                <a key={t.id} href={`/family/${t.slug || t.id}`} target="_blank" rel="noreferrer" className="flex justify-between text-sm py-1 hover:underline">
                  <span>{t.name}</span><span className="opacity-60 text-xs">{t.members} members <ExternalLink className="w-3 h-3 inline" /></span>
                </a>
              ))}
              {!(detail.trees || []).length && <div className="text-xs opacity-50">No trees</div>}
            </div>
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase opacity-60 mb-1">Payments</div>
              {(detail.payments || []).slice(0, 5).map((p) => (
                <div key={p.session_id} className="flex justify-between text-xs py-1">
                  <span>{(p.created_at || "").slice(0, 10)} · {p.plan_key} ({p.billing_period || "yearly"})</span>
                  <span className={p.payment_status === "paid" ? "text-green-700 font-semibold" : "opacity-60"}>{inr(p.amount)} · {p.payment_status}</span>
                </div>
              ))}
              {!(detail.payments || []).length && <div className="text-xs opacity-50">No payments</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailBlastModal({ segment, onClose }) {
  const [count, setCount] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.post("/admin/console/email-blast/preview", segment)
      .then((r) => setCount(r.data.count)).catch(() => setCount(0));
  }, [segment]);

  const send = async (testOnly) => {
    if (!subject.trim() || !message.trim()) { toast.error("Subject and message are required."); return; }
    if (!testOnly && !window.confirm(`Send this email to ${count} user(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await http.post("/admin/console/email-blast", { ...segment, subject, message, test_only: testOnly });
      toast.success(testOnly ? "Test email queued — check your inbox." : `Blast queued to ${data.recipients} user(s).`);
      if (!testOnly) onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send"); }
    finally { setBusy(false); }
  };

  const segLabel = [
    segment.plan_key ? `${segment.plan_key} plan` : "all plans",
    segment.near_limit ? "near limit" : null,
    segment.q ? `matching "${segment.q}"` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="fixed inset-0 z-[60] flex justify-center bg-black/60 p-4 overflow-y-auto" onClick={onClose}>
      <div className="max-w-lg w-full my-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="email-blast-modal">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-display text-2xl">Email this segment</h3>
            <div className="text-xs opacity-60 mt-0.5" data-testid="email-blast-segment-label">
              {segLabel} — {count === null ? "counting…" : <b>{count} recipient(s)</b>}
            </div>
          </div>
          <button onClick={onClose} data-testid="email-blast-close"><X className="w-5 h-5" /></button>
        </div>
        <label className="block mt-4"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Subject</div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="wofy-input" placeholder="Your family tree is almost full, {name}!" data-testid="email-blast-subject" /></label>
        <label className="block mt-3"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Message</div>
          <textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className="wofy-input" placeholder={"Namaste {name},\n\nYour family tree is growing beautifully…"} data-testid="email-blast-message" /></label>
        <p className="text-[11px] opacity-60 mt-1">Tip: <code className="font-mono">{"{name}"}</code> is replaced with each user's first name. Emails send in the background via Resend.</p>
        <div className="mt-4 flex gap-2 justify-end flex-wrap">
          <button onClick={() => send(true)} disabled={busy} className="rounded-full px-4 py-2 text-sm font-semibold border disabled:opacity-40" style={border} data-testid="email-blast-test-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Send test to me"}
          </button>
          <button onClick={() => send(false)} disabled={busy || !count} className="wofy-btn-primary disabled:opacity-40" data-testid="email-blast-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Send to {count ?? "…"} user(s)</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SubmissionsTab() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    http.get("/admin/console/submissions").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (mid, status) => {
    try {
      await http.patch(`/admin/console/memorials/${mid}/status`, { status });
      toast.success(status === "approved" ? "Memorial approved & public" : "Memorial rejected");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (items === null) return <div className="wofy-card text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!items.length) return <div className="wofy-card text-center py-10 text-sm opacity-70" data-testid="admin-submissions-empty">No memorials waiting for review. 🎉</div>;
  return (
    <div className="space-y-3" data-testid="admin-submissions-tab">
      {items.map((m) => (
        <div key={m.id} className="wofy-card flex gap-4 items-start flex-wrap" data-testid={`admin-submission-${m.id}`}>
          {m.portrait && <img src={m.portrait.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${m.portrait}` : m.portrait} alt="" className="w-14 h-14 rounded-full object-cover" />}
          <div className="flex-1 min-w-[200px]">
            <div className="font-semibold">{m.full_name}</div>
            <div className="text-xs opacity-60">{[m.location?.city, m.location?.state, m.location?.country].filter(Boolean).join(", ")} · by {m.submitter_name} · {(m.created_at || "").slice(0, 10)}</div>
            {m.short_bio && <p className="text-xs mt-1 opacity-80">{m.short_bio.slice(0, 160)}</p>}
            <a href={`/celebrate/${m.slug}`} target="_blank" rel="noreferrer" className="text-xs underline opacity-70">Preview →</a>
          </div>
          <div className="flex gap-2">
            <button onClick={() => act(m.id, "approved")} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-white" style={{ background: "#16A34A" }} data-testid={`admin-approve-${m.id}`}>
              <Check className="w-3.5 h-3.5" /> Approve
            </button>
            <button onClick={() => act(m.id, "rejected")} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold border" style={{ borderColor: "#DC2626", color: "#DC2626" }} data-testid={`admin-reject-${m.id}`}>
              <X className="w-3.5 h-3.5" /> Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function PromosTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ code: "", type: "percent", value: 20, max_uses: "", expires_at: "", plan_keys: [] });
  const PLANS = ["advance", "pro", "unlimited"];
  const togglePlan = (k) => setForm((f) => ({
    ...f, plan_keys: f.plan_keys.includes(k) ? f.plan_keys.filter((x) => x !== k) : [...f.plan_keys, k],
  }));
  const load = useCallback(() => {
    http.get("/admin/console/promo-codes").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/console/promo-codes", {
        code: form.code, type: form.type, value: Number(form.value),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
        plan_keys: form.plan_keys.length ? form.plan_keys : null,
      });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ code: "", type: "percent", value: 20, max_uses: "", expires_at: "", plan_keys: [] });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const toggle = async (code, active) => {
    await http.patch(`/admin/console/promo-codes/${code}`, { active });
    load();
  };

  const shareWhatsApp = (p) => {
    const discount = p.type === "percent"
      ? (p.value === 100 ? "completely FREE access to" : `${p.value}% off`)
      : `₹${(p.value / 100).toLocaleString("en-IN")} off`;
    const till = p.expires_at ? ` (valid till ${p.expires_at})` : "";
    const isOnam = p.code.toUpperCase().includes("ONAM");
    const msg = isOnam
      ? `🌼✨ *Onam Ashamsakal!* ✨🌼\n\nThis Onam, let's bring our whole family together on IndianFamilyTree.com — build our family tree, share old photos and create Eternal Memorials for our beloved elders.\n\n🎁 Use code *${p.code}* for ${discount} any plan${till}.\n\n👉 https://indianfamilytree.com/pricing\n\nForward this to the family group! 🛶`
      : `🎉 A family gift from IndianFamilyTree.com!\n\nBuild your family tree, preserve memories and celebrate every life.\n\n🎁 Use code *${p.code}* for ${discount} any plan${till}.\n\n👉 https://indianfamilytree.com/pricing`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="admin-promos-tab">
      <form onSubmit={create} className="wofy-card grid sm:grid-cols-5 gap-2 items-end">
        <label className="block sm:col-span-1"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Code</div>
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ONAM25" className="wofy-input" required data-testid="promo-code-input" /></label>
        <label className="block"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Type</div>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="wofy-input" data-testid="promo-type-select">
            <option value="percent">% off</option><option value="flat">₹ off (paise)</option></select></label>
        <label className="block"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">{form.type === "percent" ? "Percent (100 = free)" : "Paise"}</div>
          <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="wofy-input" required data-testid="promo-value-input" /></label>
        <label className="block"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Max uses</div>
          <input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" className="wofy-input" data-testid="promo-maxuses-input" /></label>
        <label className="block"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Expires</div>
          <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="wofy-input" data-testid="promo-expiry-input" /></label>
        <div className="sm:col-span-5">
          <div className="text-[10px] font-mono uppercase opacity-60 mb-1">Restrict to plans (none = all plans)</div>
          <div className="flex gap-1.5">
            {PLANS.map((k) => (
              <button key={k} type="button" onClick={() => togglePlan(k)}
                className="rounded-full px-3 py-1 text-xs border font-semibold capitalize"
                style={form.plan_keys.includes(k) ? { background: "var(--wofy-primary)", color: "#fff", borderColor: "var(--wofy-primary)" } : border}
                data-testid={`promo-plan-${k}`}>
                {k}{form.plan_keys.includes(k) ? " ✓" : ""}
              </button>
            ))}
          </div>
        </div>
        <button className="wofy-btn-primary sm:col-span-5 justify-center" data-testid="promo-create-btn">Create promo code</button>
      </form>
      {items === null ? <div className="wofy-card text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <div className="wofy-card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] font-mono uppercase opacity-60 border-b" style={border}>
              <th className="px-3 py-2.5">Code</th><th className="px-3 py-2.5">Discount</th><th className="px-3 py-2.5">Plans</th><th className="px-3 py-2.5">Used</th>
              <th className="px-3 py-2.5">Expires</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Share</th></tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.code} className="border-b last:border-0" style={border} data-testid={`promo-row-${p.code}`}>
                  <td className="px-3 py-2.5 font-mono font-semibold">{p.code}</td>
                  <td className="px-3 py-2.5">{p.type === "percent" ? (p.value === 100 ? "FREE (100%)" : `${p.value}%`) : inr(p.value)}</td>
                  <td className="px-3 py-2.5 text-xs capitalize">{p.plan_keys?.length ? p.plan_keys.join(", ") : "All"}</td>
                  <td className="px-3 py-2.5">{p.used_count || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                  <td className="px-3 py-2.5 text-xs">{p.expires_at || "—"}</td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => toggle(p.code, !p.active)}
                      className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                      style={p.active ? { background: "#DCFCE7", color: "#166534" } : { background: "#FEE2E2", color: "#991B1B" }}
                      data-testid={`promo-toggle-${p.code}`}>
                      {p.active ? "Active — click to disable" : "Disabled — click to enable"}
                    </button>
                  </td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => shareWhatsApp(p)}
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white"
                      style={{ background: "#25D366" }}
                      title="Share on WhatsApp"
                      data-testid={`promo-whatsapp-${p.code}`}>
                      <Share2 className="w-3 h-3" /> WhatsApp
                    </button>
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={7} className="px-3 py-6 text-center opacity-60">No promo codes yet — create one for your next festival campaign.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
